import type { Entry } from 'contentful';
import type { Dispatch, SetStateAction } from 'react';

import type { ItemUI } from '@libs/common-node';
import type { DisplayStore, SearchStore } from '@libs/common-types';
import type { Nullable } from '@libs/core';

import type { UserSessionProviderContextProps } from '../providers';

export enum PageSection {
    FEATURE = 'gift card services',
    HELP = 'help',
    HELP_CONTACT_US = 'contact us',
    HELP_CENTER = 'help center',
    LANDING = 'landing',
    LEARNING_CENTER = 'learning center',
    LEARNING_CENTER_SECTION = 'learning center section',
    LEARNING_CENTER_ARTICLE = 'learning center article',
    SHOP_BY_BRAND = 'shop by brand',
    STORE_LOCATOR = 'find a store',
    STORE_DIRECTORY = 'store directory',
    MY_ACCOUNT = 'My account',
    ORDER_SEARCH = 'order-search',
}

export enum PageType {
    ACP = 'acp',
    ACCOUNT = 'My Account',
    BRANDS = 'brands',
    CART = 'cart',
    CHECKOUT = 'checkout',
    CONFIRMATION = 'confirmation',
    CREATE_ACCOUNT = 'create account',
    DEALS = 'deals',
    FEATURE = 'feature page',
    HCP = 'hcp',
    HELP = 'help',
    HOMEPAGE = 'homepage',
    LEARNING_CENTER = 'learning center',
    PDP = 'pdp',
    PLP = 'plp',
    RESET_PASSWORD = 'reset password',
    SHOP_BY_BRAND = 'shop-by-brand',
    SIGN_IN = 'Login',
    SPECIES = 'species',
    STORES = 'stores',
    STORES_DIRECTORY = 'store directory',
    STORE_LOCATOR = 'find a store',
    TREATS_REWARDS = 'Treats Rewards',
    ORDER_SEARCH = 'account',
    NOT_FOUND = 'not found',
    CREATE_PASSWORD = 'create password',
}

type WithUserSessionProps = Omit<UserSessionProviderContextProps, 'storageUserSession'>;

export type SignInDrawerProps = {
    redirectUrl?: string;
    productToAddToWishlist?: string;
    signInTitle?: string;
    onSuccess?: () => void;
};

export enum AccountDrawerType {
    SIGN_IN = 'SIGN_IN',
    FORGOT_PASSWORD = 'FORGOT_PASSWORD',
    RESET_PASSWORD_CONFIRMATION = 'RESET_PASSWORD_CONFIRMATION',
    OFFERS = 'OFFERS',
}

export type StoreDrawerAlign = 'left' | 'right';

export type StoreDrawerOpenState = {
    pageType: string;
    align?: StoreDrawerAlign;
    showStoreDetails?: boolean;
};

export type PageLayoutContextProps = {
    adoptionCount?: number | null;
    errorAlert?: boolean;
    pageType: PageType;
    headerEntry?: Entry | null;
    storeDrawerOpen: StoreDrawerOpenState | null;
    cartStoreDrawerOpen: { item: ItemUI | undefined; open: boolean };
    fullStoreListResults: SearchStore[];
    setFullStoreListResults: Dispatch<SetStateAction<SearchStore[]>>;
    setErrorAlert: Dispatch<SetStateAction<boolean>>;
    setCartStoreDrawerOpen: Dispatch<SetStateAction<{ item: ItemUI | undefined; open: boolean }>>;
    setStoreDrawerOpen: Dispatch<SetStateAction<StoreDrawerOpenState | null>>;
    currentVariantId: number;
    setCurrentVariantId: Dispatch<SetStateAction<number>>;
    currentBopisMinimumCaseQty: number;
    setCurrentBopisMinimumCaseQty: Dispatch<SetStateAction<number>>;
    isInventoryError?: boolean;
    setIsInventoryError: Dispatch<SetStateAction<boolean>>;
    accountDrawersOpen?: AccountDrawerType[];
    isAccountDrawerOpen?: boolean;
    openAccountDrawer: (drawerId: AccountDrawerType) => void;
    removeAccountDrawers: (drawerIds: AccountDrawerType[]) => void;
    refreshNeededError?: boolean;
    setRefreshNeededError: Dispatch<SetStateAction<boolean>>;
    signInDrawerProps: SignInDrawerProps | null;
    setSignInDrawerProps: Dispatch<SignInDrawerProps | null>;
    contactUsFormStore: Nullable<DisplayStore>;
    setContactUsFormStore: Dispatch<SetStateAction<Nullable<DisplayStore>>>;
} & WithUserSessionProps;

export type PageLayoutProviderProps = {
    values: Omit<
        PageLayoutContextProps,
        | 'storeDrawerOpen'
        | 'setStoreDrawerOpen'
        | 'setErrorAlert'
        | 'currentVariantId'
        | 'setCurrentVariantId'
        | 'currentBopisMinimumCaseQty'
        | 'setCurrentBopisMinimumCaseQty'
        | 'fullStoreListResults'
        | 'setFullStoreListResults'
        | 'setIsInventoryError'
        | 'openAccountDrawer'
        | 'removeAccountDrawers'
        | 'setRefreshNeededError'
        | 'signInDrawerProps'
        | 'setSignInDrawerProps'
        | 'cartStoreDrawerOpen'
        | 'setCartStoreDrawerOpen'
        | keyof WithUserSessionProps
        | 'contactUsFormStore'
        | 'setContactUsFormStore'
    >;
};
